import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

const roleColors: Record<string, string> = {
  owner: "bg-primary/10 text-primary",
  admin: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  editor: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  viewer: "bg-muted text-muted-foreground",
};

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { activeWorkspace, workspaces, switchWorkspace, isLoading } = useWorkspace();

  if (isLoading || workspaces.length === 0) return null;

  // Don't show switcher if user only has one workspace
  if (workspaces.length === 1) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 px-2 py-1 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              <span className="hidden md:inline max-w-[120px] truncate">
                {activeWorkspace?.name}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{activeWorkspace?.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 gap-1.5 px-2"
              >
                <Building2 className="h-4 w-4" />
                <span className="hidden md:inline max-w-[120px] truncate">
                  {activeWorkspace?.name}
                </span>
                <ChevronsUpDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("workspace.switchWorkspace", "Switch Workspace")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("workspace.workspaces", "Workspaces")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((ws) => (
          <DropdownMenuItem
            key={ws.id}
            onClick={() => switchWorkspace(ws.id)}
            className="flex items-center justify-between gap-2 cursor-pointer"
          >
            <div className="flex items-center gap-2 min-w-0">
              {ws.id === activeWorkspace?.id ? (
                <Check className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <div className="h-4 w-4 shrink-0" />
              )}
              <span className="truncate">{ws.name}</span>
            </div>
            <Badge
              variant="secondary"
              className={`text-[10px] px-1.5 py-0 shrink-0 ${roleColors[ws.role] || ""}`}
            >
              {t(`workspace.role.${ws.role}`, ws.role)}
            </Badge>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
